// ---------------------------------------------------------------------------
// Sync controller: the single wiring point between auth, the SyncEngine, and the
// app. Owns the engine singleton, drives sync on local writes / a timer / coming
// back online, and exposes the handful of actions the UI calls.
// ---------------------------------------------------------------------------

import { cloudConfig, apiUrl } from '../config/cloud';
import { subscribeDb } from '../db/events';
import { getAccessToken, getAuth } from '../auth/auth';
import { HttpSyncTransport } from './client';
import { SyncEngine, type LinkMode } from './engine';
import { isSyncEnabled, getCloudUser, hasLocalUserData, outboxCount, countLocalConflicts } from './local';
import { setSyncStatus } from './status';

let engine: SyncEngine | null = null;
let started = false;
let interval: ReturnType<typeof setInterval> | null = null;

function getEngine(): SyncEngine {
  if (!engine) {
    engine = new SyncEngine(new HttpSyncTransport(cloudConfig.apiBaseUrl, getAccessToken));
  }
  return engine;
}

function signedIn(): boolean {
  return getAuth().state === 'signed-in';
}

/** True once the local DB has been linked to a cloud account. */
export function isLinked(): boolean {
  return isSyncEnabled() && getCloudUser() !== null;
}

/**
 * Wire up background sync. Called once after the DB and auth are initialized.
 * If the database is already linked, kick off an initial sync and start the
 * loops; otherwise stays dormant until the user links an account.
 */
export function startSync(): void {
  if (started) return;
  started = true;

  // Local writes -> debounced sync (only when there is something to push).
  // The pending count is surfaced immediately so the status chip reflects
  // unsynced local edits before the debounced push runs.
  subscribeDb(() => {
    if (!isLinked() || !signedIn()) return;
    const pending = outboxCount();
    setSyncStatus({ pending });
    if (pending > 0) getEngine().schedule();
  });

  // Periodic pull so changes from other devices arrive without a local write.
  interval = setInterval(() => {
    if (isLinked() && signedIn()) void getEngine().sync();
  }, 60_000);

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      if (isLinked() && signedIn()) void getEngine().sync();
    });
  }

  if (isLinked()) {
    getEngine().refreshCounts();
    setSyncStatus({ state: countLocalConflicts() > 0 ? 'conflicted' : 'synced', pending: outboxCount() });
    if (signedIn()) void resumeSync();
  } else {
    setSyncStatus({ state: 'disabled' });
  }
}

/**
 * Resume sync for an already-linked device, but only after confirming the
 * signed-in account IS the linked account. Without this, signing in with a
 * different account on a linked device would push this device's data into the
 * other account. On mismatch the device is unlinked (data stays local) and the
 * UI's link flow takes over.
 */
export async function resumeSync(): Promise<'synced' | 'mismatch' | 'skipped'> {
  if (!isLinked() || !signedIn()) return 'skipped';
  const stored = getCloudUser();
  try {
    const boot = await getEngine().bootstrapInfo();
    if (stored && boot.userId !== stored.id) {
      await unlink();
      return 'mismatch';
    }
  } catch {
    // Offline or transient failure: sync itself will park as offline/error.
  }
  void getEngine().sync();
  return 'synced';
}

/**
 * Hosted-web sign-in: the cloud account is primary and sync is always on, so
 * the browser links automatically with no first-sync choice. A fresh browser
 * takes the cloud copy; otherwise merge, which preserves both sides.
 */
export async function autoLinkForWeb(): Promise<void> {
  const boot = await getEngine().bootstrapInfo();
  await linkAccount(boot.hasCloudData && !hasLocalUserData() ? 'useCloud' : 'merge');
}

export function stopSync(): void {
  if (interval) clearInterval(interval);
  interval = null;
  started = false;
}

/** Link the local database to the signed-in account and run the first sync. */
export async function linkAccount(mode: LinkMode): Promise<void> {
  await getEngine().linkAccount(mode);
  startSync();
}

/** Manual "Sync now". */
export async function syncNow(): Promise<void> {
  if (isLinked() && signedIn()) await getEngine().sync();
}

/** Stop cloud sync locally (keeps all local data; does not delete the cloud account). */
export async function unlink(): Promise<void> {
  await getEngine().unlink();
}

export function listConflicts() {
  return getEngine().listConflicts();
}

export async function resolveConflict(id: string, resolution: 'mine' | 'cloud' | 'merge', mergedPayload: string | null): Promise<void> {
  await getEngine().resolveConflict(id, resolution, mergedPayload);
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new Error('Please sign in first.');
  return fetch(apiUrl(path), {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` },
  });
}

/** Everything the cloud holds for this account (for a user-facing data export). */
export async function exportCloudAccount(): Promise<unknown> {
  const res = await authedFetch('/api/account/export');
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  return res.json();
}

/** Permanently delete the cloud account and stop syncing locally (keeps local data). */
export async function deleteCloudAccount(): Promise<void> {
  const res = await authedFetch('/api/account', { method: 'DELETE' });
  if (!res.ok) throw new Error(`Delete failed (${res.status})`);
  await unlink();
}
